import React, { useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from 'utils/hooks';

import { CATALOG_SELECTOR_DATA } from 'utils/globals';

import crops from 'domains/crops';
import ui from 'domains/ui';
import navigation from 'domains/navigation';
import cartProductCreator from 'domains/cart_product_creator';

import { useWosRouting } from 'utils/hooks';

import CatalogSelector from 'components/pages/CatalogSelector';
import WOSSiteHeader from 'components/features/WOSSiteHeader';
import PriceTool from 'components/pages/PriceTool';
import CartPage from 'components/pages/CartPage';
import EditorPage from 'components/pages/EditorPage';
import Projects from 'components/pages/Projects';
import SiteMessage from 'components/shared/SiteMessage';

const categories = CATALOG_SELECTOR_DATA;

function Wos() {
  /*
   * Redux values
   */
  const dispatch = useAppDispatch();
  const potentialCropIds = useAppSelector(crops.selectors.potentialCropIds);
  const addToCartSuccess = useAppSelector(cartProductCreator.selectors.wasSuccessful);
  const addToCartWasEdit = useAppSelector(cartProductCreator.selectors.wasEdit);
  const hasMobileNavigation = useAppSelector(ui.selectors.hasMobileNavigation);

  const showAdvancedFilters = useAppSelector(ui.selectors.showAdvancedFilters);
  const siteMessage = useAppSelector(ui.selectors.siteMessage);
  const hideBranding = useAppSelector(ui.selectors.hideBranding);
  const showFilenames = useAppSelector(ui.selectors.showFilenames);
  const theme = useAppSelector(ui.selectors.theme);

  // Routing
  const showCatalogSelector = useAppSelector(navigation.selectors.showCatalogSelector);
  const showEditor = useAppSelector(navigation.selectors.showEditor);
  const showCart = useAppSelector(navigation.selectors.showCart);
  const showPriceTool = useAppSelector(navigation.selectors.showPriceTool);
  const showProjects = useAppSelector(navigation.selectors.showProjects);

  // Custom hook for handling history state
  useWosRouting();

  // Reconcile crops with potential cropIds, if product changes and no longer has same image node UID
  useEffect(() => {
    dispatch(crops.actions.reconcileCrops());
  }, [dispatch, potentialCropIds]);

  useEffect(() => {
    if (showCatalogSelector) {
      dispatch(crops.actions.resetCrops());
    }
  }, [dispatch, showCatalogSelector]);

  useEffect(() => {
    if (addToCartSuccess) {
      dispatch(cartProductCreator.actions.resetAddToCartSuccess());

      if (addToCartWasEdit || hasMobileNavigation) {
        dispatch(navigation.actions.navigateTo('cart'));
      }
    }
  }, [dispatch, addToCartSuccess, addToCartWasEdit, hasMobileNavigation]);

  const siteMessageEnabled = useMemo(() => siteMessage.enabled, [siteMessage]);

  function closeSiteMessage() {
    dispatch(ui.actions.setSiteMessage({}));
  }

  const containerClassnames = useMemo(() => {
    const classNames = [
      { on: siteMessageEnabled, name: 'show-site-message' },
      { on: hideBranding, name: 'hideBranding' },
      { on: showFilenames, name: 'showFilenames' },
      { on: showCatalogSelector, name: 'show-catalog-selector' },
    ];

    return classNames
      .filter((c) => c.on)
      .map((c) => c.name)
      .join(' ');
  }, [siteMessageEnabled, hideBranding, showFilenames, showCatalogSelector]);

  return (
    <div className={`wos_container ${containerClassnames}`}>
      <WOSSiteHeader />
      {siteMessage.enabled && (
        <SiteMessage siteMessage={siteMessage} closeSiteMessage={closeSiteMessage} />
      )}
      <div
        id="main-content"
        className={`main_content theme-${theme}${hasMobileNavigation ? ' mobile' : ''}${
          showAdvancedFilters ? ' advanced-filters' : ''
        }`}
      >
        {showCatalogSelector && <CatalogSelector categories={categories || []} />}
        {showCart && <CartPage />}
        {showProjects && <Projects />}
        {showPriceTool && <PriceTool />}
        {showEditor && <EditorPage />}
      </div>
    </div>
  );
}

export default Wos;
